import { useId, useMemo, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Command, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import type { RecaptureOrigin } from '@/services/recaptureService';

interface Props {
  origins: RecaptureOrigin[];
  selectedOrigin: RecaptureOrigin | null;
  disabled: boolean;
  onSelect: (payoutId: string) => void;
}

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export function RecaptureOriginPicker({ origins, selectedOrigin, disabled, onSelect }: Props) {
  const labelId = useId();
  const anchor = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const expanded = open && !disabled;
  const matches = useMemo(() => {
    const search = query.trim().toLowerCase();
    return origins.filter(origin => [origin.loan_number, origin.borrower_name, origin.recipient_name]
      .some(value => value?.toLowerCase().includes(search)))
      .sort((a, b) => b.loan_number.localeCompare(a.loan_number));
  }, [origins, query]);
  const results = matches.slice(0, 25);
  const focusInput = () => anchor.current?.querySelector('input')?.focus();

  return <div className="min-w-0 space-y-2">
    <Label id={labelId} onClick={focusInput}>Search paid commissions</Label>
    <Command shouldFilter={false} loop className="h-auto overflow-visible rounded-md bg-transparent">
      <Popover open={expanded} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div ref={anchor} className="min-w-0 overflow-hidden rounded-md border border-input bg-background">
            <CommandInput asChild
              placeholder="Loan #, borrower, or loan officer" disabled={disabled} value={query}
              onFocus={() => setOpen(true)} onClick={() => setOpen(true)}
              onValueChange={value => {
                setQuery(value);
                setOpen(true);
              }}
              onKeyDown={event => {
                if (event.key === 'Escape') {
                  event.preventDefault();
                  event.stopPropagation();
                  setOpen(false);
                } else if (event.key === 'Tab') {
                  setOpen(false);
                } else if (!expanded && ['ArrowUp', 'ArrowDown', 'Enter'].includes(event.key)) {
                  event.preventDefault();
                  setOpen(true);
                }
              }}>
              <input aria-labelledby={labelId} aria-expanded={expanded}
                {...(!expanded ? { 'aria-activedescendant': undefined } : {})} />
            </CommandInput>
          </div>
        </PopoverAnchor>
        <PopoverContent align="start" aria-label="Paid commission search results" className="w-[--radix-popover-trigger-width] p-0"
          onOpenAutoFocus={event => event.preventDefault()}
          onCloseAutoFocus={event => event.preventDefault()}
          onEscapeKeyDown={event => {
            event.preventDefault();
            setOpen(false);
            focusInput();
          }}
          onInteractOutside={event => {
            if (anchor.current?.contains(event.target as Node)) {
              event.preventDefault();
            }
          }}>
          <CommandList label="Paid banked commissions" className="max-h-[min(300px,var(--radix-popover-content-available-height))] p-1">
            {results.length === 0 && <p className="px-2 py-6 text-center text-sm">No paid banked commission matches.</p>}
            {results.map(origin => <CommandItem key={origin.payout_id} value={origin.payout_id}
              disabled={disabled} onSelect={() => {
                if (!disabled) {
                  setOpen(false);
                  setQuery('');
                  onSelect(origin.payout_id);
                }
              }}>
              <Check aria-hidden="true" className={selectedOrigin?.payout_id === origin.payout_id ? 'opacity-100' : 'opacity-0'} />
              <span className="min-w-0 flex-1 space-y-1">
                <span className="block break-all font-medium">{origin.loan_number}</span>
                <span className="block break-words">{origin.borrower_name || 'Borrower unavailable'}</span>
                <span className="block break-words text-xs text-muted-foreground">{origin.recipient_name}</span>
                <span className="flex flex-wrap gap-x-2 text-xs text-muted-foreground">
                  <span>Gross {currency.format(origin.gross_commission)}</span>
                  {origin.fund_date && <span>Funded <time dateTime={origin.fund_date}>{origin.fund_date}</time></span>}
                </span>
              </span>
            </CommandItem>)}
          </CommandList>
          {matches.length > 25 && <p className="border-t px-3 py-2 text-xs text-muted-foreground">Keep typing to narrow {matches.length - 25} more</p>}
        </PopoverContent>
      </Popover>
    </Command>
    <p role="status" aria-live="polite" aria-atomic="true" className="sr-only">
      {expanded ? `${matches.length} matching paid commissions. Showing ${results.length}.` : ''}
    </p>
    {selectedOrigin && <div className="flex min-w-0 flex-wrap items-center justify-between gap-2 text-sm">
      <p className="min-w-0 break-words">{selectedOrigin.loan_number} · {selectedOrigin.recipient_name}</p>
      <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => {
        setQuery('');
        setOpen(true);
        focusInput();
      }}>Change loan</Button>
    </div>}
  </div>;
}
